import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Api, HttpClient } from '../../generated';
import { API_BASE_URL } from '../../config/api';

// Swagger artık ItemBomDto'yu schema olarak yayınlamıyor (yalnızca GET/DELETE rotaları var); şekil ItemBomDto.cs ile birebir.
export interface ItemBomDto {
  id?: number;
  itemCode?: string;
  itemName?: string | null;
  componentItemCode?: string;
  componentItemName?: string | null;
  quantity?: number;
  createdAt?: string | Date;
  createdBy?: number;
  updatedAt?: string | Date | null;
  updatedBy?: number | null;
}
export type ItemBom = ItemBomDto;

// Create API client instance compatible with generated API
const apiClient = new HttpClient({
  baseURL: API_BASE_URL,
  securityWorker: async () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      return {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
    }
    return {};
  },
  secure: true,
});

const api = new Api(apiClient);

interface ItemBomState {
  loading: boolean;
  itemBoms: ItemBom[];
  selectedItemBom: ItemBom | null;
  error: string | null;
  operationLoading: {
    create: boolean;
    update: boolean;
    delete: boolean;
  };
}

const initialState: ItemBomState = {
  loading: false,
  itemBoms: [],
  selectedItemBom: null,
  error: null,
  operationLoading: {
    create: false,
    update: false,
    delete: false,
  },
};

// Async thunks for ItemBom operations
export const getItemBoms = createAsyncThunk(
  "itemBom/getItemBoms",
  async (params: any = {}, thunkAPI) => {
    try {
      const response = await api.api.itemBomList(params);
      return response;
    } catch (error) {
      
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getItemBomById = createAsyncThunk(
  "itemBom/getItemBomById",
  async (id: number, thunkAPI) => {
    try {
      const response = await api.api.itemBomDetail({ id });
      return response;
    } catch (error) {
      
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteItemBom = createAsyncThunk(
  "itemBom/deleteItemBom",
  async (id: number, thunkAPI) => {
    try {
      const response = await api.api.itemBomDelete({ id });
      return response;
    } catch (error) {
      
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const itemBomSlice = createSlice({
  name: "itemBom",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearItemBoms: (state) => {
      state.itemBoms = [];
      state.error = null;
    },
    clearSelectedItemBom: (state) => {
      state.selectedItemBom = null;
    },
    setSelectedItemBom: (state, action) => {
      state.selectedItemBom = action.payload;
    },
  },
  extraReducers(builder) {
    // Get ItemBoms
    builder.addCase(getItemBoms.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getItemBoms.fulfilled, (state, action) => {
      state.loading = false;
      // Handle response structure
      const responseData = action.payload;
      if (Array.isArray(responseData)) {
        state.itemBoms = responseData;
      } else if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        const data = (responseData as any).data;
        if (Array.isArray(data)) {
          state.itemBoms = data;
        } else if (data && data.itemBoms) {
          state.itemBoms = data.itemBoms;
        }
      }
      state.error = null;
    });
    builder.addCase(getItemBoms.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'ItemBom listesi yüklenemedi';
    });

    // Get ItemBom By ID
    builder.addCase(getItemBomById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getItemBomById.fulfilled, (state, action) => {
      state.loading = false;
      const responseData = action.payload;
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        state.selectedItemBom = (responseData as any).data;
      } else {
        state.selectedItemBom = responseData as ItemBom;
      }
      state.error = null;
    });
    builder.addCase(getItemBomById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'ItemBom detayları yüklenemedi';
    });

    // Delete ItemBom
    builder.addCase(deleteItemBom.pending, (state) => {
      state.operationLoading.delete = true;
      state.error = null;
    });
    builder.addCase(deleteItemBom.fulfilled, (state, action) => {
      state.operationLoading.delete = false;
      // Remove itemBom from the list
      const id = action.meta.arg;
      state.itemBoms = state.itemBoms.filter(item => item.id !== id);
      if (state.selectedItemBom?.id === id) {
        state.selectedItemBom = null;
      }
      state.error = null;
    });
    builder.addCase(deleteItemBom.rejected, (state, action) => {
      state.operationLoading.delete = false;
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'ItemBom silinemedi';
    });
  },
});

export const { 
  clearError, 
  clearItemBoms, 
  clearSelectedItemBom, 
  setSelectedItemBom 
} = itemBomSlice.actions;

export default itemBomSlice.reducer;
